import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Spinner } from '../ui/spinner';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { ToggleGroup, ToggleItem } from '../ui/toggle';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import { SortableHead, TablePagination } from '../game_department/TableControls';
import GameTitleButton from '../game_department/GameTitleButton';
import useBrandChange from '../game_department/useBrandChange';
import { gameAnalysisAPI } from '../../services/api';
import {
  PAGE_SIZE, channelMeta, defaultSortDir, formatCompleteness, formatDate, formatMoney, formatPct, formatX, gamesQuery, humanize,
  jackpotCell,
} from './analysisData';

const SEARCH_DEBOUNCE_MS = 300;

function GameRow({ g, onOpen }) {
  const rtpChannel = g.rtp_channel ? channelMeta(g.rtp_channel) : null;
  const jackpot = jackpotCell(g);
  return (
    <TableRow onClick={() => onOpen(g)} className="cursor-pointer">
      <TableCell className="min-w-[200px]">
        <GameTitleButton title={g.title} onActivate={() => onOpen(g)} label={`Open the analysis of ${g.title}`} />
        <p className="text-[11px] text-ink-mute">{g.provider}</p>
      </TableCell>
      <TableCell className="text-right whitespace-nowrap">
        {formatPct(g.rtp)}
        {rtpChannel && <p className="text-[11px] text-ink-mute">{rtpChannel.label}</p>}
      </TableCell>
      <TableCell className="text-right whitespace-nowrap">{formatX(g.max_win_x)}</TableCell>
      <TableCell className="text-ink-dim">{g.volatility ? humanize(g.volatility) : '—'}</TableCell>
      <TableCell className="whitespace-nowrap text-ink-dim">
        {g.min_bet_eur != null ? `${formatMoney(g.min_bet_eur)} – ${formatMoney(g.max_bet_eur)}` : '—'}
      </TableCell>
      <TableCell className="text-right whitespace-nowrap">
        {jackpot ? jackpot.text : '—'}
        {jackpot?.tiers && <p className="text-[11px] text-ink-mute">{jackpot.tiers}</p>}
      </TableCell>
      <TableCell className="text-right">{g.bonus_buys || '—'}</TableCell>
      <TableCell className="text-right">{g.currencies || '—'}</TableCell>
      <TableCell className="text-right whitespace-nowrap">
        {g.facts_of ? `${g.facts_have}/${g.facts_of}` : '—'}
      </TableCell>
      <TableCell className="text-right">{formatCompleteness(g.completeness)}</TableCell>
      <TableCell className="max-w-[180px]">
        <div className="flex flex-wrap gap-1">
          {g.primary_type && <Badge variant="secondary">{humanize(g.primary_type)}</Badge>}
          {(g.themes || []).slice(0, 2).map(t => <Badge key={t} variant="outline">{humanize(t)}</Badge>)}
        </div>
      </TableCell>
      <TableCell className="whitespace-nowrap text-ink-dim">{formatDate(g.assembled_at)}</TableCell>
      <TableCell className="whitespace-nowrap">
        {g.failures > 0 && <Badge variant="destructive" className="mr-1">{g.failures} failed</Badge>}
        {g.warnings > 0 && <Badge variant="warning">{g.warnings} skipped</Badge>}
        {!g.failures && !g.warnings && <span className="text-ink-mute">—</span>}
      </TableCell>
    </TableRow>
  );
}

export default function GameAnalysisPage() {
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [total, setTotal] = useState(0);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [provider, setProvider] = useState('all');
  const [sortCol, setSortCol] = useState('assembled_at');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [problemsOnly, setProblemsOnly] = useState(false);
  const [jackpot, setJackpot] = useState('all');
  const [jackpotTiers, setJackpotTiers] = useState(0);

  const activeBrand = useBrandChange(() => {
    setProvider('all');
    setPage(1);
  });

  // the search box filters on the server; wait for the user to stop typing
  useEffect(() => {
    const t = setTimeout(() => { setQuery(search); setPage(1); }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    gameAnalysisAPI.getProviders()
      .then(res => { if (!cancelled) setProviders(res.data.providers || []); })
      .catch(() => { if (!cancelled) setProviders([]); });
    return () => { cancelled = true; };
  }, [activeBrand]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    gameAnalysisAPI.getGames(gamesQuery({ query, provider, sortCol, sortDir, page, problemsOnly, jackpot, jackpotTiers }), controller.signal)
      .then(res => {
        setGames(res.data.games || []);
        setTotal(res.data.total || 0);
      })
      .catch(err => {
        if (controller.signal.aborted) return;
        setGames([]);
        setTotal(0);
        setError(err.response?.data?.error || 'Could not load the analysed games.');
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [activeBrand, query, provider, sortCol, sortDir, page, problemsOnly, jackpot, jackpotTiers]);

  const onSort = col => {
    if (col === sortCol) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir(defaultSortDir(col)); }
    setPage(1);
  };
  const sortProps = { sortCol, sortDir, onSort };
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const open = g => navigate(`/game-analysis/${encodeURIComponent(g.slug)}`);

  return (
    <div className="max-w-[1400px] mx-auto">
      <h2 className="font-display text-lg font-bold text-ink mb-1">Game Analysis</h2>
      <p className="text-xs text-ink-dim mb-4">
        Games captured and analysed in real-money sessions: rules, bet ladders per currency, bonus buys and
        key facts. Every value carries its source — measured from the game, stated in its rules, from the
        catalog, or read by a model.
      </p>

      <Card>
        <CardContent className="p-5">
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div className="relative">
              <label className="text-[11px] text-ink-dim mb-1 block" htmlFor="game-analysis-search">Search</label>
              <Search className="absolute left-2.5 bottom-2.5 h-4 w-4 text-ink-mute" />
              <Input
                id="game-analysis-search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Title, slug or provider"
                className="w-[260px] pl-8"
              />
            </div>
            <div>
              <label className="text-[11px] text-ink-dim mb-1 block">Provider</label>
              <Select value={provider} onValueChange={v => { setProvider(v); setPage(1); }}>
                <SelectTrigger className="w-[200px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All providers</SelectItem>
                  {providers.map(p => (
                    <SelectItem key={p.provider} value={p.provider}>{p.provider} ({p.games})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label htmlFor="ga-jackpot" className="text-[11px] text-ink-dim mb-1 block">Jackpot</label>
              <Select value={jackpot} onValueChange={v => { setJackpot(v); setPage(1); }}>
                <SelectTrigger id="ga-jackpot" className="w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All games</SelectItem>
                  <SelectItem value="any">Any jackpot</SelectItem>
                  <SelectItem value="fixed">Fixed</SelectItem>
                  <SelectItem value="progressive">Progressive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label htmlFor="ga-jackpot-tiers" className="text-[11px] text-ink-dim mb-1 block">Jackpot tiers</label>
              <Select value={String(jackpotTiers)} onValueChange={v => { setJackpotTiers(Number(v)); setPage(1); }}>
                <SelectTrigger id="ga-jackpot-tiers" className="w-[110px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Any</SelectItem>
                  {[2, 3, 4, 5].map(n => <SelectItem key={n} value={String(n)}>{n}+ tiers</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] text-ink-dim mb-1 block">Show</label>
              <ToggleGroup value={problemsOnly ? 'problems' : 'all'} onChange={v => { setProblemsOnly(v === 'problems'); setPage(1); }} className="h-9">
                <ToggleItem value="all">All games</ToggleItem>
                <ToggleItem value="problems">With problems</ToggleItem>
              </ToggleGroup>
            </div>
            {loading && <Spinner size="sm" className="mb-2.5" />}
          </div>

          {error && (
            <Alert variant="error" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <SortableHead col="title" label="Game" {...sortProps} />
                <SortableHead col="rtp" label="RTP" right {...sortProps} />
                <SortableHead col="max_win_x" label="Max win" right {...sortProps} />
                <TableHead>Volatility</TableHead>
                <TableHead className="whitespace-nowrap">Bets (EUR)</TableHead>
                <SortableHead col="jackpot" label="Jackpot" right {...sortProps} />
                <TableHead className="text-right whitespace-nowrap">Bonus buys</TableHead>
                <TableHead className="text-right">Currencies</TableHead>
                <TableHead className="text-right">Facts</TableHead>
                <SortableHead col="completeness" label="Capture" right {...sortProps} />
                <TableHead>Type / theme</TableHead>
                <SortableHead col="assembled_at" label="Analysed" {...sortProps} />
                <SortableHead col="issues" label="Issues" {...sortProps} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {games.map(g => <GameRow key={g.slug} g={g} onOpen={open} />)}
              {!loading && games.length === 0 && !error && (
                <TableRow>
                  <TableCell colSpan={13} className="text-center text-ink-dim py-8">No analysed games match.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination page={page} totalPages={totalPages} total={total} onPageChange={setPage} />
        </CardContent>
      </Card>
    </div>
  );
}
